import React from 'react';
import { getTaskStatusColor } from '../../utils/taskUtils';
import { formatDateUser } from '../../utils/dateUtils';

const KanbanCard = ({ task, onClick }) => {
  const extractMeetLinkFromText = (text) => {
    if (!text || typeof text !== 'string') return null;
    const meetRegex = /(https?:\/\/(?:meet\.google\.com|hangouts\.google\.com|calendar\.google\.com\/event[^\s]*|zoom\.us\/[^\s]+|teams\.microsoft\.com\/[^\s]+)[^\s]*)/i;
    const match = text.match(meetRegex);
    return match ? match[0] : null;
  };

  const description = task?.description || '';
  const subtaskCount = Array.isArray(task.subtasks) ? task.subtasks.length : 0;
  const completionPercent = Number.isFinite(task.completionPercentage)
    ? Math.max(0, Math.min(100, task.completionPercentage))
    : 0;

  const truncateByChars = (text, max = 160) => {
    if (!text) return '';
    if (text.length <= max) return text;
    return `${text.slice(0, max - 1).trim()}…`;
  };

  const linkifyDescription = (text) => {
    if (!text) return '';
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const parts = text.split(urlRegex);
    return parts.map((part, idx) => {
      if (!part) return null;
      if (/^(https?:\/\/[^\s]+)$/i.test(part)) {
        return (
          <a
            key={`kanban-link-${idx}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return <span key={`kanban-text-${idx}`}>{part}</span>;
    });
  };

  const departmentName = task.department?.name || task.department || 'Not assigned';
  const customerName = task.customer?.name || 'N/A';

  const meetingLink =
    task?.meetingLink ||
    task?.meetingURL ||
    task?.meetingUrl ||
    task?.meetUrl ||
    task?.hangoutLink ||
    task?.calendar?.hangoutLink ||
    task?.meeting?.hangoutLink ||
    extractMeetLinkFromText(task?.description);

  const statusColor = getTaskStatusColor(task.status);
  const truncatedDescription = truncateByChars(description);
  const renderedDescription = linkifyDescription(truncatedDescription);

  const getAssignedName = () => {
    if (task?.supervisor) {
      const first = task.supervisor.firstName || '';
      const last = task.supervisor.lastName || '';
      const full = `${first} ${last}`.trim();
      return full || task.supervisor.email || 'Unassigned';
    }
    return 'Unassigned';
  };
const getFieldStaffName = () => {
  if (task?.assignedEmployee) {
    const first = task.assignedEmployee.firstName || '';
    const last = task.assignedEmployee.lastName || '';
    const full = `${first} ${last}`.trim();
    return full || task.assignedEmployee.email || 'Unassigned';
  }

  return 'Unassigned';
};
  const getResolutionDisplay = () => {
    const statusLower = (task?.status || '').toLowerCase();
    if (statusLower !== 'resolved' || !task?.resolvedAt) return null;
    const value = formatDateUser(task.resolvedAt);
    let duration = '';
    if (task?.startedAt) {
      const resolved = new Date(task.resolvedAt).getTime();
      const started = new Date(task.startedAt).getTime();
      if (Number.isFinite(resolved) && Number.isFinite(started) && resolved >= started) {
        const diffMs = resolved - started;
        const minMs = 60000;
        const hourMs = 3600000;
        const dayMs = 86400000;
        if (diffMs < hourMs) {
          const mins = Math.max(1, Math.round(diffMs / minMs));
          duration = ` (in ${mins} ${mins === 1 ? 'min' : 'mins'})`;
        } else if (diffMs < dayMs) {
          const hrs = Math.round(diffMs / hourMs);
          duration = ` (in ${hrs} ${hrs === 1 ? 'hr' : 'hrs'})`;
        } else {
          const days = Math.round(diffMs / dayMs);
          duration = ` (in ${days} ${days === 1 ? 'day' : 'days'})`;
        }
      }
    }
    return { label: 'Resolved', value: `${value}${duration}` };
  };
  const getDueLabel = () => {
    if (!task?.dueDate) return { label: '-', cls: '' };
    const statusLower = (task?.status || '').toLowerCase();
    const now = new Date();
    const due = new Date(task.dueDate);
    const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dueMid = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const diffDays = Math.round((dueMid.getTime() - todayMid.getTime()) / 86400000);
    const isClosed = statusLower === 'resolved' || statusLower === 'rejected';
    if (diffDays < 0 && isClosed) {
      // hide overdue for resolved/rejected; fall back to due date
      return { label: formatDateUser(task.dueDate), cls: '' };
    }
    if (diffDays < 0) return { label: 'Overdue', cls: 'due-overdue' };
    if (diffDays === 0) return { label: 'Today', cls: 'due-today' };
    if (diffDays === 1) return { label: 'Tomorrow', cls: 'due-tomorrow' };
    return { label: formatDateUser(task.dueDate), cls: '' };
  };

  const { label: dueLabel, cls: dueCls } = getDueLabel();
  const resolutionMeta = getResolutionDisplay();
  const assignedName = getAssignedName();
  const fieldStaffName = getFieldStaffName();
  return (
    <div
      className="kanban-mini-card compact enhanced"
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Open task ${task.id}`}
      style={{ '--kmc-status-color': statusColor }}
    >
      <div className="kmc-header">
        <div className="kmc-left">
          <span className="kmc-id">#{task.id}</span>
          {task.priority && (
            <span className={`kmc-badge priority-${task.priority.toLowerCase()}`}>{task.priority}</span>
          )}
          {departmentName && departmentName !== 'Not assigned' && (
            <span className="kmc-badge team-badge">{departmentName}</span>
          )}
        </div>
        <span className="kmc-status" style={{ '--kmc-status-color': statusColor }}>{task.status?.replace('-', ' ')}</span>
      </div>
      <div className="kmc-title" title={description}>
        {renderedDescription}
      </div>
      <div className="kmc-meta">
        {typeof task?.taskType === 'string' && task.taskType.toLowerCase() === 'meeting' && meetingLink && (
          <div className="kmc-meta-row">
            <span className="kmc-label">Meeting</span>
            <a
              className="btn-secondary small"
              href={meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ marginLeft: 6 }}
            >
              Join meeting
            </a>
          </div>
        )}
        <div className="kmc-meta-row">
          <span className="kmc-label">Customer</span>
          <span className="kmc-value">{customerName}</span>
        </div>
        <div className="kmc-meta-row">
          <span className="kmc-label">Assigned</span>
          <span className="kmc-value">{assignedName}</span>
        </div>
        {fieldStaffName!=='Unassigned' && (
        <div className="kmc-meta-row">
          <span className="kmc-label">Field Staff</span>
          <span className="kmc-value">{fieldStaffName}</span>
        </div>
        )}
        {resolutionMeta ? (
          <div className="kmc-meta-row">
            <span className="kmc-label">{resolutionMeta.label}</span>
            <span className="kmc-value">{resolutionMeta.value}</span>
          </div>
        ) : (
          <div className="kmc-meta-row">
            <span className="kmc-label">Due</span>
            <span className={`kmc-value ${dueCls}`}>{dueLabel}</span>
          </div>
        )}
        {subtaskCount>0 && (
        <div className="kmc-meta-row">
            <span className="kmc-label">Subtasks</span>
            <span className="kmc-value">{subtaskCount}</span>
          </div>
        )}
      </div>
      <div className="kmc-progress" style={{ marginTop: 5 }} aria-label={`Completion ${completionPercent}%`}>
        <div 
          className="progress-bar" 
          role="progressbar" 
          aria-valuenow={completionPercent} 
          aria-valuemin="0" 
          aria-valuemax="100"
          style={{ height: 4 }}
        >
          <div 
            className="progress-fill" 
            style={{ width: `${completionPercent}%` }}
          />
        </div>
        <span className="progress-text" style={{ fontSize: 10 }}>{completionPercent}%</span>
      </div>
    </div>
  );
};

export default KanbanCard;
